/**
 * The signeringslista — a medication administration record on paper (PHASE-0.md §2.6).
 *
 * **What this sheet is for.** Phase 0 records nothing about administration: there is no
 * ordination, no administrering and no electronic signing of a dose given (`PHASE-0.md`
 * §4, `ROADMAP.md` puts it in phase 2). During an outage the doses still have to be given
 * and signed for, so the printed sheet with a pen is the compensating control
 * `ARCHITECTURE.md` §12 names. This is that sheet, printed from the record instead of
 * copied out of it by hand.
 *
 * **§2.6's acceptance criterion is that it looks like a hospital document, not like a web
 * page**, and three things carry that. The app chrome is not printed — banner, navigation
 * and footer are suppressed in `@media print`, and the document supplies its own header
 * and provenance footer instead. The safety block is on the first page above the orders,
 * which is `AGENTS.md` §5's *without scrolling* rule translated to paper. And the
 * identification line repeats on every page, so a sheet that continues cannot be read as
 * belonging to the patient whose name happened to be on the page before it.
 *
 * **One `<tbody>` per order, which is load-bearing.** Each order is two rows — the order
 * itself, then its signing strip — and those two must never be split across a page break,
 * because a strip of empty boxes at the top of a page with the drug name overleaf is the
 * exact shape of a dose signed against the wrong order. A `<tbody>` is the smallest
 * grouping a browser will keep together with `break-inside: avoid`; `<tr>` on its own does
 * not group the pair. Several `<tbody>` elements in one table is valid HTML, and the
 * `<thead>` is repeated on each page by the browser, which is what gives the running
 * identification line for free.
 *
 * Everything clinical comes from the same functions the overview screen uses —
 * `safetyPanel`, `medicationPreparation`, `medicationSchedule`, the shared dose and date
 * formatters. The one deliberate difference is the dose failure path: see
 * `printableDose`.
 */

import { formatClinicalDateOnly } from '../format/clinicalDate'
import { guarded, patientName } from '../patient/patientDisplay'
import type { PatientOverview } from '../patient/patientOverviewApi'
import {
  medicationPreparation,
  medicationSchedule,
  practitionerName,
  safetyPanel,
} from '../patient/patientOverviewDisplay'
import { patientLocation } from '../patient/patientSearchDisplay'
import {
  SIGNING_BOXES,
  identifierLines,
  printableDose,
  printedAtLine,
  unprintableDoses,
  wristbandIdentifier,
  type PrintTranslate,
} from './printDisplay'

/** The number of columns in the order table. The signing strip spans all of them. */
const ORDER_COLUMNS = 6

export function AdministrationRecord({
  overview,
  now,
  t,
}: {
  readonly overview: PatientOverview

  /**
   * When this sheet was produced.
   *
   * Passed in rather than read here, so the footer's timestamp is the one moment the
   * document was built and does not change if React re-renders it. A printed page that
   * cannot be dated is a page nobody can place in a sequence afterwards.
   */
  readonly now: Date

  readonly t: PrintTranslate
}) {
  const location = overview.location
  const birthDate = overview.birthDate
  const name = patientName(overview, t)

  // The identifier for the running header: the same one the wristband encodes, so a
  // sheet and a band can be held side by side and compared on one number.
  const leading = wristbandIdentifier(overview)
  const identification = t('print:mar.identification', {
    name,
    identifier: leading?.value ?? t('print:mar.noIdentifier'),
  })

  const unprintable = unprintableDoses(overview)

  return (
    <article className="print-document print-mar">
      <header className="print-document__header">
        {/*
          Provenance before anything else. A loose sheet found on a trolley has to say
          which system it came out of: a note written here does not exist in the central
          EHR until it is handed back, and neither does a dose signed on this paper.
        */}
        <p className="print-document__source">{t('print:document.source')}</p>
        <h1 className="print-document__title">{t('print:mar.heading')}</h1>

        <div className="print-document__patient">
          <p className="print-document__name">{name}</p>

          <dl className="print-document__facts">
            {/* Every identifier, not only the national one — the reservnummer is what
                yesterday's notes and the bed sign say, and a sheet that carries only the
                personnummer cannot be matched against them. */}
            {identifierLines(overview, t).map((line) => (
              <div key={line.key}>
                <dt>{line.label}</dt>
                <dd>{line.value}</dd>
              </div>
            ))}

            <div>
              <dt>{t('patient:overview.birthDate')}</dt>
              <dd>
                {birthDate === null
                  ? t('common:value.notRecorded')
                  : guarded(() => formatClinicalDateOnly(birthDate), t)}
              </dd>
            </div>

            <div>
              <dt>{t('patient:overview.ward')}</dt>
              <dd>{location?.wardName ?? t('common:value.notRecorded')}</dd>
            </div>

            <div>
              <dt>{t('patient:overview.location')}</dt>
              <dd>
                {patientLocation(
                  { ward: location?.ward ?? null, bed: location?.bed ?? null },
                  t,
                )}
              </dd>
            </div>

            <div>
              <dt>{t('patient:overview.responsible')}</dt>
              <dd>
                {location === null
                  ? t('common:value.notRecorded')
                  : practitionerName(
                      location.responsibleGivenName,
                      location.responsibleFamilyName,
                      t,
                    )}
              </dd>
            </div>
          </dl>
        </div>
      </header>

      {/*
        First page, above the orders. `AGENTS.md` §5's rule is *visible without
        scrolling*; on paper that is *on the first page, before the thing you came to
        read*. The same five slots as the screen, from the same function, so the sheet
        cannot say one thing and the screen another.

        Level is carried by rule weight and by the words, not by colour: a ward printer
        is monochrome and a red border that prints grey says nothing.
      */}
      <section className="print-document__safety" aria-label={t('print:mar.safetyHeading')}>
        <h2>{t('print:mar.safetyHeading')}</h2>

        <dl className="print-safety">
          {safetyPanel(overview, t).map((entry) => (
            <div key={entry.key} className={`print-safety__slot print-safety__slot--${entry.level}`}>
              <dt>{entry.label}</dt>
              <dd>
                <ul>
                  {entry.rows.map((row, index) => (
                    // The index is the key for the reason `PatientOverview.tsx` gives: a
                    // row has no identity of its own and two flags of a type can carry
                    // the same brief.
                    <li key={index}>
                      <span className="print-safety__value">{row.value}</span>
                      {row.detail !== null && (
                        <span className="print-safety__detail">{row.detail}</span>
                      )}
                      {row.attribution !== null && (
                        <span className="print-safety__attribution">{row.attribution}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="print-mar__orders">
        <h2>{t('print:mar.medicationsHeading')}</h2>

        {/*
          Said at the top of the sheet and not only in the cell. A nurse signing a box is
          confirming they gave what the row says; if the row cannot state the dose, that
          has to be visible before they start rather than discovered halfway down.
        */}
        {unprintable.length > 0 && (
          <p className="print-mar__dose-warning">
            {t('print:mar.doseWarning', { count: unprintable.length })}
          </p>
        )}

        {overview.medications.length === 0 ? (
          <p className="print-mar__none">{t('print:mar.noMedications')}</p>
        ) : (
          <>
            <p className="print-mar__hint">{t('print:mar.signingHint')}</p>

            <table className="print-mar__table">
              <caption className="visually-hidden">{t('print:mar.caption')}</caption>

              {/*
                Repeated by the browser on every page, which is the whole reason the
                identification line lives in here rather than above the table.
              */}
              <thead>
                <tr className="print-mar__running">
                  <th scope="colgroup" colSpan={ORDER_COLUMNS}>
                    {identification}
                  </th>
                </tr>
                <tr>
                  <th scope="col">{t('patient:medication.columnSubstance')}</th>
                  <th scope="col">{t('patient:medication.columnPreparation')}</th>
                  <th scope="col">{t('patient:medication.columnDose')}</th>
                  <th scope="col">{t('patient:medication.columnRoute')}</th>
                  <th scope="col">{t('patient:medication.columnSchedule')}</th>
                  <th scope="col">{t('patient:medication.columnIndication')}</th>
                </tr>
              </thead>

              {overview.medications.map((medication) => {
                const dose = printableDose(medication)

                return (
                  // One tbody per order — see this file's header. Not a style choice.
                  <tbody key={medication.medicationId} className="print-mar__order">
                    <tr>
                      <td className="print-mar__substance">{medication.substance}</td>
                      <td>{medicationPreparation(medication, t)}</td>
                      <td
                        className={
                          dose === null
                            ? 'print-mar__dose print-mar__dose--unprintable'
                            : 'print-mar__dose'
                        }
                      >
                        {dose ?? t('print:mar.doseUnprintable')}
                      </td>
                      <td>{medication.route}</td>
                      <td>{medicationSchedule(medication, t)}</td>
                      <td>{medication.indication}</td>
                    </tr>

                    <tr className="print-mar__signing">
                      <td colSpan={ORDER_COLUMNS}>
                        <span className="print-mar__signing-label">
                          {t('print:mar.columnSigning')}
                        </span>
                        <span className="print-mar__boxes">
                          {/*
                            Blank, and undated. `SIGNING_BOXES` says why: printing a time
                            grid would mean this code had turned the prescriber's words
                            into a schedule.
                          */}
                          {Array.from({ length: SIGNING_BOXES }, (_unused, index) => (
                            <span key={index} className="print-mar__box" />
                          ))}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                )
              })}
            </table>

            <p className="print-mar__hint">{t('print:mar.notInRecord')}</p>
          </>
        )}
      </section>

      <footer className="print-document__footer">
        <p>{printedAtLine(now, t)}</p>
        <p>{t('print:document.verify')}</p>
      </footer>
    </article>
  )
}
